import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Lock, Check, AlertCircle, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";

// Reset Password screen — reached from the emailed link
// (/reset-password?token=...). Posts to /api/auth/password-reset-confirm.
// Public (no auth cookie needed).

export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (!token) {
      setError("This reset link is missing its token. Request a new email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset-confirm", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't reset password. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="screen fade-in" data-testid="reset-password-screen">
      <div className="top-bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate("/")}
          aria-label="Back"
          data-testid="reset-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">RESET</div>
        <div style={{ width: 36 }} />
      </div>

      {!done ? (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>NEW PASSWORD</div>
          <h2 className="h1" style={{ fontSize: 30, marginBottom: 14, lineHeight: 1.12 }}>
            Choose a<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>fresh password.</em>
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 22 }}>
            Pick something you&apos;ll remember. This password works on the website, iPhone and Android apps.
          </p>

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="field">
              <label className="label" htmlFor="new-pass">New password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="new-pass"
                  type={showPw ? "text" : "password"}
                  required
                  autoFocus
                  autoComplete="new-password"
                  className="input"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-new-password-input"
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="reset-new-password-toggle"
                  style={{
                    position: "absolute", right: 10, top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent", border: "none",
                    color: "var(--text-dim)", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 6, borderRadius: 6,
                  }}
                >
                  {showPw ? <EyeOff size={16} strokeWidth={1.7} /> : <Eye size={16} strokeWidth={1.7} />}
                </button>
              </div>
            </div>
            <div className="field">
              <label className="label" htmlFor="confirm-pass">Confirm password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="confirm-pass"
                  type={showConfirm ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  className="input"
                  placeholder="Type it again"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password-input"
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  data-testid="reset-confirm-password-toggle"
                  style={{
                    position: "absolute", right: 10, top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent", border: "none",
                    color: "var(--text-dim)", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 6, borderRadius: 6,
                  }}
                >
                  {showConfirm ? <EyeOff size={16} strokeWidth={1.7} /> : <Eye size={16} strokeWidth={1.7} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="error-text"
                data-testid="reset-error"
                style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}
              >
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              data-testid="reset-submit-btn"
              style={{ marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              {submitting ? (
                <>
                  <span className="spinner" />
                  <span>Updating password…</span>
                </>
              ) : (
                <>
                  <Lock size={15} strokeWidth={1.8} />
                  <span>Update password</span>
                </>
              )}
            </button>
          </form>
        </>
      ) : (
        <div style={{ textAlign: "center", paddingTop: 60 }} data-testid="reset-success">
          <div
            style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "rgba(232,211,185,0.15)", color: "var(--gold)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <Check size={28} strokeWidth={1.8} />
          </div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.15 }}>
            Password updated.
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", maxWidth: 320, margin: "0 auto" }}>
            You can now sign in with your new password on the website, iPhone and Android apps.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/", { replace: true })}
            data-testid="reset-back-to-login"
            style={{ marginTop: 28, width: "100%", justifyContent: "center" }}
          >
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
}
